import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { LayoutGrid, CreditCard, LogOut, BarChart3, Building2, LifeBuoy, Menu, X } from "lucide-react";
import { LOGO_MARK } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import HelpAssistant from "@/components/HelpAssistant";
import ReviewPrompt from "@/components/ReviewPrompt";

const PLAN_NAME = { free: "Free", growth: "Growth", scale: "Scale" };

const AppShell = ({ children }) => {
  const { user, tenant, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const [afp, setAfp] = useState(null);
  const seenKey = `bs_afp_seen:${user?.tenant_id || "anon"}`;

  useEffect(() => {
    api.get("/applications/summary").then(({ data }) => setAfp(data)).catch(() => {});
  }, []);

  const afpBadge = afp && afp.latest_at && localStorage.getItem(seenKey) !== afp.latest_at ? afp.total : 0;
  const markBillingSeen = () => { if (afp?.latest_at) localStorage.setItem(seenKey, afp.latest_at); setAfp((p) => (p ? { ...p } : p)); };

  const nav = [
    { to: "/app", label: "Projects", icon: LayoutGrid },
    { to: "/app/settings", label: "Settings", icon: Building2 },
    { to: "/app/account", label: "Billing & Plan", icon: CreditCard },
    { to: "/app/help", label: "Help Centre", icon: LifeBuoy },
    ...(user?.is_admin ? [{ to: "/app/insights", label: "Insights", icon: BarChart3 }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 h-14 bg-white border-b border-slate-200 flex items-center gap-3 px-4">
        <button onClick={() => setNavOpen(true)} data-testid="mobile-nav-toggle" aria-label="Open menu" className="text-slate-600 hover:text-slate-900">
          <Menu className="h-6 w-6" />
        </button>
        <img src={LOGO_MARK} alt="TradeOff" className="h-7 w-7 object-contain" />
        <span className="font-display text-lg font-bold text-navy">TradeOff</span>
      </div>

      {/* Mobile backdrop */}
      {navOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setNavOpen(false)} data-testid="mobile-nav-backdrop" />
      )}

      {/* Sidebar (off-canvas drawer on mobile, sticky on desktop) */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col h-screen transition-transform duration-200 lg:sticky lg:top-0 lg:z-30 lg:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-200 shrink-0">
          <img src={LOGO_MARK} alt="TradeOff" className="h-8 w-8 object-contain" />
          <span className="font-display text-lg font-bold text-navy">TradeOff</span>
          <button onClick={() => setNavOpen(false)} aria-label="Close menu" className="ml-auto lg:hidden text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((n) => {
            const active = location.pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => { setNavOpen(false); if (n.to === "/app/account") markBillingSeen(); }}
                data-testid={`sidebar-${n.label.split(" ")[0].toLowerCase()}`}
                className={`relative flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  active ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <n.icon className="h-4 w-4" /> {n.label}
                {n.to === "/app/account" && afpBadge > 0 && (
                  <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="sidebar-billing-badge">{afpBadge > 9 ? "9+" : afpBadge}</span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200 shrink-0">
          <div className="px-3 py-2 rounded-md bg-slate-50 mb-2">
            {tenant?.company_name && (
              <p className="text-sm font-semibold text-slate-800 truncate mb-1.5" data-testid="sidebar-company">{tenant.company_name}</p>
            )}
            <p className="text-xs text-slate-500">Current plan</p>
            <p className="text-sm font-semibold text-navy" data-testid="sidebar-plan">
              {PLAN_NAME[tenant?.plan] || "Free"}
            </p>
          </div>
          <div className="flex items-center gap-2 px-2">
            <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="text-slate-400 hover:text-red-500">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 pb-16 lg:pb-0">
        {tenant?.trial_active && tenant?.trial_ends_at && location.pathname !== "/app/account" && (() => {
          const days = Math.max(0, Math.ceil((new Date(tenant.trial_ends_at) - new Date()) / 86400000));
          const label = days <= 0 ? "ends today" : days === 1 ? "1 day left" : `${days} days left`;
          return (
            <Link
              to="/app/account"
              data-testid="trial-countdown-banner"
              className="block bg-blue-600 text-white text-sm text-center px-4 py-2 hover:bg-blue-700 transition-colors"
            >
              <span className="font-semibold">{label}</span> on your {tenant.trial_plan_name || "free"} trial — <span className="underline">choose a plan</span> to keep your jobs running
            </Link>
          );
        })()}
        {children}
      </main>

      {/* Mobile bottom tab bar */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 flex"
        data-testid="mobile-bottom-nav"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {[
          { to: "/app", label: "Projects", icon: LayoutGrid },
          { to: "/app/settings", label: "Settings", icon: Building2 },
          { to: "/app/account", label: "Billing", icon: CreditCard },
        ].map((b) => {
          const active = location.pathname === b.to;
          return (
            <Link
              key={b.to}
              to={b.to}
              onClick={() => { if (b.to === "/app/account") markBillingSeen(); }}
              data-testid={`bottomnav-${b.label.toLowerCase()}`}
              className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors ${
                active ? "text-blue-600" : "text-slate-500"
              }`}
            >
              <b.icon className="h-5 w-5" />
              {b.label}
              {b.to === "/app/account" && afpBadge > 0 && (
                <span className="absolute top-1.5 right-[calc(50%-1.25rem)] min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="bottomnav-billing-badge">{afpBadge > 9 ? "9+" : afpBadge}</span>
              )}
            </Link>
          );
        })}
      </nav>

      <HelpAssistant />
      <ReviewPrompt summary={afp} />
    </div>
  );
};

export default AppShell;
